import { FC, ReactNode } from "react";

import {
  ExpandablePanel,
  MarkdownDiv,
  SourceCodePanel,
} from "@tsmono/react/components";

import styles from "./customToolRendering.module.css";
import { parseToolSearchCatalog, toolOutputText } from "./tool";
import type { ToolCallViewProps } from "./ToolCallView";
import { ToolSearchView } from "./ToolSearchView";
import { ToolTitle } from "./ToolTitle";

/**
 * Default custom tool views built into the shared package.
 * Apps can provide additional custom views via the getCustomToolView prop.
 */
export const getDefaultCustomToolView = (
  props: ToolCallViewProps
): ReactNode | undefined => {
  if (props.tool === "answer") {
    return <AnswerToolCallView {...props} />;
  }
  if (props.tool === "submit") {
    return <SubmitToolCallView {...props} />;
  }
  if (props.tool === "tool_search") {
    const namespaces = parseToolSearchCatalog(props.output);
    if (namespaces) {
      return <ToolSearchView namespaces={namespaces} />;
    }
  }
  return undefined;
};

const AnswerToolCallView: FC<ToolCallViewProps> = (props) => {
  return (
    <SourceCodePanel
      code={props.functionCall}
      language="python"
      id={props.id}
    />
  );
};

// The submit tool echoes its `answer` argument as its result, so the default
// ToolCallView (input panel + output panel) would render the answer twice.
// Render it once, as markdown — submit answers are usually prose/bullets — while
// keeping a recognizable title so a submit stays distinct from a normal turn.
const SubmitToolCallView: FC<ToolCallViewProps> = (props) => {
  // getDefaultCustomToolView already committed to this view, so there is no
  // fall-through to the default rendering — default to an empty string (rather
  // than returning nothing) so an empty answer still shows the submit title.
  const answer =
    toolOutputText(props.output) ?? toolOutputText(props.input) ?? "";
  return (
    <div className={styles.submitView}>
      {props.mode !== "compact" ? (
        <div>
          <ToolTitle title="submit" />
        </div>
      ) : null}
      <ExpandablePanel
        id={`${props.id}-submit`}
        collapse={false}
        border={false}
        lines={15}
        className={"text-size-small"}
      >
        <MarkdownDiv markdown={answer} />
      </ExpandablePanel>
    </div>
  );
};
